import {Accordion, Alert, Badge, Card, Col, Form, Row} from "react-bootstrap";
import PropTypes from "prop-types";
import AddressAutocompleteField from "./AddressAutocompleteField";
import {DAY_LABELS, normalizeWorkerProfile, speedOption, SPEED_OPTIONS} from "./workerProfileModel";

export default function WorkerProfileEditor({value, onChange, region, skills, serviceAreas, catalogWarning}) {
    const profile = normalizeWorkerProfile(value);
    const speed = speedOption(profile.speed_tier);
    const selectedSkillIds = new Set(profile.skill_ids);
    const assignmentByAreaId = new Map(profile.service_areas.map(item => [item.service_area_id, item]));
    const selectedAreaIds = new Set(assignmentByAreaId.keys());
    const visibleSkills = skills.filter(item => item.status === 'ACTIVE' || selectedSkillIds.has(item.id));
    const visibleAreas = serviceAreas.filter(item => item.operational_region === region || selectedAreaIds.has(item.id));
    const incompatibleAreas = visibleAreas.filter(item => selectedAreaIds.has(item.id)
        && item.operational_region !== region);

    function update(patch) {
        onChange({...profile, ...patch});
    }

    function updatePreference(field, source, address = null) {
        update({[field]: {source, address: source === 'CUSTOM' ? address : null}});
    }

    function updateDay(index, patch) {
        update({
            working_hours: profile.working_hours.map((day, dayIndex) => dayIndex === index
                ? {...day, ...patch}
                : day),
        });
    }

    function updateDayPreference(index, field, source, address = null) {
        updateDay(index, {[field]: {source, address: source === 'CUSTOM' ? address : null}});
    }

    function toggleSkill(skillId) {
        update({
            skill_ids: selectedSkillIds.has(skillId)
                ? profile.skill_ids.filter(id => id !== skillId)
                : [...profile.skill_ids, skillId],
        });
    }

    function toggleArea(areaId) {
        update({
            service_areas: selectedAreaIds.has(areaId)
                ? profile.service_areas.filter(item => item.service_area_id !== areaId)
                : [...profile.service_areas, {service_area_id: areaId, tier: 1}],
        });
    }

    function updateAreaTier(areaId, tier) {
        update({
            service_areas: profile.service_areas.map(item => item.service_area_id === areaId
                ? {...item, tier: Number(tier)}
                : item),
        });
    }

    return (
        <Card className="mt-3 worker-profile-editor">
            <Card.Header className="d-flex justify-content-between align-items-center">
                <span className="fw-semibold">Worker Profile</span>
                <Badge bg="secondary">Storage & display only</Badge>
            </Card.Header>
            <Card.Body>
                {catalogWarning ? <Alert variant="warning">{catalogWarning}</Alert> : null}
                <Row>
                    <Col lg={4}>
                        <Form.Group className="mb-3" controlId="worker-speed-tier">
                            <Form.Label>Speed</Form.Label>
                            <Form.Select
                                value={profile.speed_tier}
                                onChange={event => update({speed_tier: event.target.value})}
                            >
                                {SPEED_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label} (×{option.coefficient.toFixed(2)})
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Text>Future effective time: Property duration × {speed.coefficient.toFixed(2)}.</Form.Text>
                        </Form.Group>
                    </Col>
                    <Col lg={8}>
                        <AddressAutocompleteField
                            label="Home Address"
                            value={profile.home_address}
                            onChange={address => update({home_address: address})}
                            region={region}
                        />
                    </Col>
                </Row>

                <Row>
                    {[
                        ['default_start_address', 'Default Start Address'],
                        ['default_end_address', 'Default End Address'],
                    ].map(([field, label]) => {
                        const preference = profile[field];
                        const custom = preference?.source === 'CUSTOM';
                        return (
                            <Col lg={6} key={field}>
                                <Form.Check
                                    type="switch"
                                    className="mb-2"
                                    id={`${field}-custom`}
                                    label={`Use a custom ${label.toLowerCase()}`}
                                    checked={custom}
                                    onChange={event => updatePreference(field,
                                        event.target.checked ? 'CUSTOM' : 'HOME', preference?.address)}
                                />
                                {custom ? (
                                    <AddressAutocompleteField
                                        label={label}
                                        value={preference?.address}
                                        onChange={address => updatePreference(field, 'CUSTOM', address)}
                                        region={region}
                                        required
                                    />
                                ) : <p className="small text-muted">Uses Home Address.</p>}
                            </Col>
                        );
                    })}
                </Row>

                <h2 className="h6 mt-3">Weekly Working Hours</h2>
                <Accordion className="mb-4">
                    {profile.working_hours.map((day, index) => (
                        <Accordion.Item key={day.day_of_week} eventKey={day.day_of_week}>
                            <Accordion.Header>
                                <span className="me-2">{DAY_LABELS[day.day_of_week] || day.day_of_week}</span>
                                <Badge bg={day.enabled ? 'success' : 'secondary'}>
                                    {day.enabled ? `${day.start_time}–${day.end_time}` : 'Not working'}
                                </Badge>
                            </Accordion.Header>
                            <Accordion.Body>
                                <Row className="align-items-end">
                                    <Col md={3}>
                                        <Form.Check
                                            type="switch"
                                            className="mb-3"
                                            id={`${day.day_of_week}-enabled`}
                                            label="Working"
                                            checked={day.enabled}
                                            onChange={event => updateDay(index, {enabled: event.target.checked})}
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Start</Form.Label>
                                            <Form.Control
                                                type="time"
                                                step="1800"
                                                value={day.start_time}
                                                disabled={!day.enabled}
                                                onChange={event => updateDay(index, {start_time: event.target.value})}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>End</Form.Label>
                                            <Form.Control
                                                type="time"
                                                step="1800"
                                                value={day.end_time}
                                                disabled={!day.enabled}
                                                onChange={event => updateDay(index, {end_time: event.target.value})}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    {[
                                        ['start_address', 'Start Address'],
                                        ['end_address', 'End Address'],
                                    ].map(([field, label]) => {
                                        const preference = day[field];
                                        const custom = preference?.source === 'CUSTOM';
                                        return (
                                            <Col lg={6} key={field}>
                                                <Form.Check
                                                    type="switch"
                                                    id={`${day.day_of_week}-${field}-custom`}
                                                    className="mb-2"
                                                    label={`Custom ${label.toLowerCase()}`}
                                                    disabled={!day.enabled}
                                                    checked={custom}
                                                    onChange={event => updateDayPreference(index, field,
                                                        event.target.checked ? 'CUSTOM' : 'DEFAULT', preference?.address)}
                                                />
                                                {custom ? (
                                                    <AddressAutocompleteField
                                                        label={`${DAY_LABELS[day.day_of_week]} ${label}`}
                                                        value={preference?.address}
                                                        onChange={address => updateDayPreference(index, field, 'CUSTOM', address)}
                                                        region={region}
                                                        required
                                                    />
                                                ) : null}
                                            </Col>
                                        );
                                    })}
                                </Row>
                            </Accordion.Body>
                        </Accordion.Item>
                    ))}
                </Accordion>

                <Row>
                    <Col lg={5}>
                        <h2 className="h6">Skills</h2>
                        <div className="worker-profile-choice-list mb-4">
                            {visibleSkills.map(skill => (
                                <Form.Check
                                    key={skill.id}
                                    id={`worker-skill-${skill.id}`}
                                    type="checkbox"
                                    checked={selectedSkillIds.has(skill.id)}
                                    disabled={skill.status !== 'ACTIVE' && !selectedSkillIds.has(skill.id)}
                                    onChange={() => toggleSkill(skill.id)}
                                    label={<>{skill.name}{skill.status !== 'ACTIVE' ? <Badge bg="secondary" className="ms-2">Inactive</Badge> : null}</>}
                                />
                            ))}
                            {visibleSkills.length === 0 ? <p className="text-muted small mb-0">No Skills configured.</p> : null}
                        </div>
                    </Col>
                    <Col lg={7}>
                        <h2 className="h6">Service Areas</h2>
                        {incompatibleAreas.length > 0 ? (
                            <Alert variant="warning" className="py-2">
                                {incompatibleAreas.length} selected area(s) belong to another Region and must be removed when saving.
                            </Alert>
                        ) : null}
                        <div className="worker-profile-choice-list mb-2">
                            {visibleAreas.map(area => {
                                const selected = selectedAreaIds.has(area.id);
                                const assignment = assignmentByAreaId.get(area.id);
                                const incompatible = area.operational_region !== region;
                                return (
                                    <div key={area.id} className="worker-area-choice">
                                        <Form.Check
                                            id={`worker-area-${area.id}`}
                                            type="checkbox"
                                            checked={selected}
                                            disabled={(area.status !== 'ACTIVE' || incompatible) && !selected}
                                            onChange={() => toggleArea(area.id)}
                                            label={`${area.name} ${area.postcode}`}
                                        />
                                        {selected ? (
                                            <Form.Select
                                                size="sm"
                                                aria-label={`Tier for ${area.name}`}
                                                value={assignment?.tier || 1}
                                                onChange={event => updateAreaTier(area.id, event.target.value)}
                                            >
                                                <option value={1}>Tier 1</option>
                                                <option value={2}>Tier 2</option>
                                                <option value={3}>Tier 3</option>
                                            </Form.Select>
                                        ) : null}
                                        {area.status !== 'ACTIVE' ? <Badge bg="secondary">Inactive</Badge> : null}
                                        {incompatible ? <Badge bg="warning" text="dark">{area.operational_region}</Badge> : null}
                                    </div>
                                );
                            })}
                            {visibleAreas.length === 0 ? <p className="text-muted small mb-0">No Service Areas configured for {region}.</p> : null}
                        </div>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
}

WorkerProfileEditor.propTypes = {
    value: PropTypes.object,
    onChange: PropTypes.func.isRequired,
    region: PropTypes.string,
    skills: PropTypes.array.isRequired,
    serviceAreas: PropTypes.array.isRequired,
    catalogWarning: PropTypes.string,
};
